import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaBullseye,
  FaEye,
  FaAward,
  FaHandshake,
  FaRocket,
  FaUsers,
  FaGlobe,
  FaGraduationCap,
  FaBuilding,
  FaChartLine,
  FaStar,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import RoadmapTimeline from "../components/RoadmapTimeline";

const WhoWeAre = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  const teamMembers = [
    {
      name: "Faculty Member",
      role: "Senior Language Instructor",
      department: "French & German",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
      placeholder: true,
    },
    {
      name: "Faculty Member",
      role: "Senior Language Instructor",
      department: "Spanish & Japanese",
      image:
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
      placeholder: true,
    },
    {
      name: "Management Team",
      role: "Director of Operations",
      department: "Administration",
      image:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
      placeholder: true,
    },
    {
      name: "Management Team",
      role: "Academic Director",
      department: "Curriculum Development",
      image:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
      placeholder: true,
    },
  ];

  const accreditations = [
    {
      name: "ISO 9001:2015",
      description: "Quality Management System Certified",
    },
    { name: "Goethe Institute", description: "Authorized Test Center" },
    { name: "Instituto Cervantes", description: "DELE Examination Partner" },
    { name: "Alliance Française", description: "DELF/DALF Certified Center" },
    { name: "JLPT", description: "Japanese Language Proficiency Test Center" },
  ];

  const partners = [
    { name: "Partner University 1", description: "Academic Partnership" },
    { name: "Corporate Partner 1", description: "Corporate Training Partner" },
    { name: "Partner University 2", description: "Student Exchange Program" },
    { name: "Corporate Partner 2", description: "Language Services Provider" },
  ];

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-primary/10 py-10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${process.env.PUBLIC_URL}/AboutUs1.png')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/80 to-black/45" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-primary/10" />
        
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-6 px-4 py-20 text-center lg:px-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-primary-soft/30 rounded-full blur-2xl animate-pulse" />
            <h1 className="relative text-5xl font-semibold sm:text-6xl text-white pb-2 drop-shadow-lg">
              Building Global Communicators Since 2018
            </h1>
          </div>
          <p className="max-w-3xl text-lg leading-relaxed text-white/90 sm:text-xl drop-shadow-md">
            We are a collective of certified linguists, instructional designers,
            and cultural mentors united by the mission to build confident
            communicators without borders.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              About Us
            </span>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">
              Our Story
            </h2>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="space-y-6 text-ink-muted leading-relaxed">
              <p className="text-base sm:text-lg">
                Founded in 2018, The Lingua Alliance emerged from a simple yet
                powerful vision: to break down language barriers and empower
                individuals to communicate confidently across cultures. What
                started as a small language training center has grown into a
                comprehensive language education institution, serving thousands
                of students across multiple countries.
              </p>
              <p className="text-base sm:text-lg">
                Our journey began when our founders recognized a gap in quality
                language education that combined rigorous academic standards
                with practical, real-world application. We set out to create
                programs that not only teach languages but also immerse students
                in the cultural contexts that make communication meaningful and
                effective.
              </p>
              <p className="text-base sm:text-lg">
                Today, we stand as a trusted partner for individuals,
                corporations, and educational institutions seeking excellence in
                language learning. Our commitment to innovation, quality, and
                student success has earned us recognition from leading
                certification bodies and partnerships with prestigious
                institutions worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Timeline Section */}
      <RoadmapTimeline />

      {/* Mission & Vision Section */}
      <section className="py-20 bg-canvas">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl border border-primary/10 bg-white p-8 shadow-elevated">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-2xl text-primary mb-6">
                <FaBullseye />
              </div>
              <h3 className="text-2xl font-semibold text-ink mb-4">
                Our Mission
              </h3>
              <p className="text-ink-muted leading-relaxed">
                To empower learners worldwide with exceptional language
                education that opens doors to global opportunities. We are
                committed to providing accessible, high-quality language
                training that combines academic excellence with cultural
                immersion, enabling our students to communicate confidently and
                succeed in an interconnected world.
              </p>
            </div>

            <div className="rounded-2xl border border-primary/10 bg-white p-8 shadow-elevated">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-2xl text-primary mb-6">
                <FaEye />
              </div>
              <h3 className="text-2xl font-semibold text-ink mb-4">
                Our Vision
              </h3>
              <p className="text-ink-muted leading-relaxed">
                To become the world's most trusted language education
                institution, recognized for innovation, excellence, and
                transformative learning experiences. We envision a future where
                language barriers no longer limit human potential, and where
                every learner has access to world-class language education that
                enriches their personal and professional lives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Our Team
            </span>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">
              Meet Our Experts
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-ink-muted">
              Our dedicated faculty and management team bring years of
              experience and passion for language education
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)]"
              >
                <div className="relative mb-4 aspect-square overflow-hidden rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  {member.placeholder && (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary/5">
                      <span className="text-xs font-medium text-primary/60">
                        Photo TBD
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-ink mb-1">{member.name}</h3>
                <p className="text-sm text-primary font-medium mb-1">
                  {member.role}
                </p>
                <p className="text-xs text-ink-muted">{member.department}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditations Section */}
      <section className="py-20 bg-canvas">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Accreditations
            </span>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">
              Recognized & Certified
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-ink-muted">
              Our certifications and partnerships reflect our commitment to
              excellence
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5 mb-12">
            {accreditations.map((accreditation, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated transition-all duration-300 hover:-translate-y-1 hover:shadow-float text-center"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-xl text-primary mb-4 group-hover:scale-110 transition-transform">
                  <FaAward />
                </div>
                <h3 className="font-semibold text-ink mb-2">
                  {accreditation.name}
                </h3>
                <p className="text-xs text-ink-muted">
                  {accreditation.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Our Partners
            </span>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">
              Trusted Partnerships
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-ink-muted">
              Collaborating with leading institutions and organizations
              worldwide
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated transition-all duration-300 hover:-translate-y-1 hover:shadow-float text-center"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-xl text-primary mb-4 group-hover:scale-110 transition-transform">
                  <FaHandshake />
                </div>
                <h3 className="font-semibold text-ink mb-2">{partner.name}</h3>
                <p className="text-xs text-ink-muted">{partner.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-canvas">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/5 via-white to-white/80 p-8 shadow-[0_28px_60px_rgba(15,23,42,0.18)]">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold mb-4">Join Our Team</h2>
              <p className="text-ink-muted">
                We're always looking for passionate educators and professionals
                to join our mission of building global communicators
              </p>
            </div>
            <Link
              to="/career"
              className="block w-full text-center rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)]"
            >
              Join Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeAre;
